import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, ElementRef, inject, Injector, input, signal, viewChild } from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { NeoFormFieldModule } from '@neogov/pattern-library/neo-form-field';
import { NeoInputModule } from '@neogov/pattern-library/neo-input';
import {
  FieldErrorsComponent,
  OuterControlErrorStateMatcher,
  shouldSurfaceFieldError,
} from '../field-errors';

export type InputType = 'text' | 'number' | 'password' | 'email' | 'time';
export type InputValue = string | number | null;

@Component({
  selector: 'cm-input-field',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NeoFormFieldModule,
    NeoInputModule,
    FieldErrorsComponent,
  ],
  templateUrl: './input-field.component.html',
  styleUrl: './input-field.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: InputFieldComponent,
      multi: true,
    },
  ],
})
export class InputFieldComponent implements ControlValueAccessor {
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _nativeInputRef = viewChild<ElementRef<HTMLInputElement>>('nativeInput');

  /** The underlying native `<input>` element — exposed for focus comparisons. */
  public get nativeInputEl(): HTMLInputElement | undefined {
    return this._nativeInputRef()?.nativeElement;
  }

  public readonly label = input<string>();
  public readonly description = input<string>('');
  public readonly ariaLabel = input<string>('');
  public readonly placeholder = input<string>('');
  public readonly autId = input.required<string>();
  public readonly type = input<InputType>('text');
  /** HTML autocomplete token so browsers/AT can identify the field's purpose (WCAG 1.3.5). */
  public readonly autocomplete = input<string | null>(null);
  public readonly hideMessages = input<boolean>(false);
  public readonly hint = input<string>('');
  public readonly min = input<number | null>(null);
  public readonly max = input<number | null>(null);
  public readonly maxlength = input<number | null>(null);
  public readonly minlength = input<number | null>(null);
  public readonly required = input<boolean>(false);
  public readonly showCount = input<boolean>(false);

  // Error handling inputs
  public readonly errorMessages = input<Partial<Record<string, string>>>({});
  public readonly showErrorsWhen = input<'touched' | 'dirty' | 'always'>('touched');
  public readonly hideErrors = input<boolean>(false);

  public value: InputValue = null;
  public disabled = false;

  protected readonly _valueLength = signal<number>(0);
  protected readonly countText = computed(() => {
    const max = this.maxlength();
    return max != null ? `${this._valueLength()}/${max}` : `${this._valueLength()}`;
  });

  private readonly _injector = inject(Injector);
  public readonly ngControl = signal<NgControl | null>(null);

  /**
   * Feeds the OUTER control's validity into the native input so the pattern
   * library exposes aria-invalid and links the error via aria-describedby
   * (WCAG 3.3.1 / 4.1.2). See {@link OuterControlErrorStateMatcher}.
   */
  protected readonly errorStateMatcher = new OuterControlErrorStateMatcher(() =>
    shouldSurfaceFieldError(this.ngControl()?.control, this.showErrorsWhen(), this.hideErrors())
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onChange: (value: InputValue) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched: () => void = () => {};

  constructor() {
    // Defer NgControl lookup to avoid circular dependency
    // The NgControl directive will be available after the component is constructed
    queueMicrotask(() => {
      try {
        const ngControl =
          this._injector.get(NgControl, null, { optional: true, self: true }) || null;
        this.ngControl.set(ngControl);
      } catch {
        this.ngControl.set(null);
      }
    });
  }

  public writeValue(value: InputValue): void {
    this.value = value;
    this._valueLength.set(String(value ?? '').length);
    this._cdr.markForCheck();
  }

  public registerOnChange(fn: (value: InputValue) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._cdr.markForCheck();
  }

  public onInputChange(value: string): void {
    const inputType = this.type();
    if (inputType === 'number') {
      this.value = value ? Number(value) : null;
    } else {
      this.value = value;
    }
    this._valueLength.set(value.length);
    this.onChange(this.value);
  }

  public onBlur(): void {
    // Simply call onTouched - no setTimeout needed!
    // The cm-field-errors component is subscribed to control.events
    // which emits TouchedChangeEvent synchronously when markAsTouched() is called
    this.onTouched();
  }

  /** Inserts text at the current cursor position (or replaces the selection). */
  public insertAtCursor(text: string): void {
    const input = this._nativeInputRef()?.nativeElement;
    if (!input) return;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    input.focus();
    input.setSelectionRange(start, end);
    // execCommand keeps the browser undo stack intact; falls back to direct mutation
    if (!document.execCommand('insertText', false, text)) {
      const newValue = input.value.slice(0, start) + text + input.value.slice(end);
      input.value = newValue;
      input.setSelectionRange(start + text.length, start + text.length);
      this.onChange(newValue);
      this.onTouched();
    }
  }
}
